/**
 * A-26 — the manufacturing board's two reading rules, pure so they are tested without a screen.
 *
 * Both exist because of one defect. "Close production" was handed the `Order` object that was
 * snapshotted into React state when its row was drawn, so once made quantities had been saved the
 * dialog still listed a fully made order's every line as short: on MK-1051, "Line 1: 2 of 2 would
 * be short" for all three lines of an order that was complete. It then closed the order fine and
 * the order went READY — the data was right and only the dialog was wrong. Reading that, an admin
 * believes they are about to write off a finished order, cancels, and never ships it.
 *
 * So a dialog holds an order's ID and resolves the order again on every render. Nothing on this
 * board may keep a copy of a figure the server owns.
 */

/** Enough of a board line to say what closing production would write off. */
export interface ClosableLine {
  lineNumber: number;
  ordered: number;
  /** Still to make, as the server last answered. Zero on a made line and zero once closed. */
  outstanding: number;
}

/**
 * The order a dialog is open on, from the CURRENT board — null once the board no longer carries
 * it, which closes a dialog whose order has moved on rather than leaving a stale one open.
 */
export function currentOrder<T extends { id: string }>(
  orders: readonly T[], id: string | null,
): T | null {
  if (id === null) return null;
  return orders.find((o) => o.id === id) ?? null;
}

/**
 * MF-06 — the lines closing production now would make a final shortfall of.
 *
 * `outstanding` is already the server's own derivation (`lineProgress` in the API's
 * `manufacturing.board.ts`): ordered − made while production is open, and 0 once it is closed.
 * A made line therefore has nothing here, and a genuinely short one has its real figure.
 */
export function shortfallLines<T extends ClosableLine>(lines: readonly T[]): T[] {
  return lines.filter((l) => l.outstanding > 0);
}
